<script setup>
import { useTranslations } from "@/composables/translations";
import Item from "./Item.vue";
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useApp } from "@/stores/app";
import { inject } from "vue";

const { translate } = useTranslations();

const priceOverviewHeaderFixed = inject("priceOverviewHeaderFixed", true);

const appStore = useApp();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  priceOverview: {
    type: Object,
    required: false,
  },
});

const isFreeTicket = computed(() => {
  return props.priceOverview.total && props.priceOverview.total === "€ 0,00";
});

const serviceFeeIsZero = computed(() => {
  return (
    props.priceOverview.serviceFee &&
    props.priceOverview.serviceFee === "€ 0,00"
  );
});

// Add refs for card and header
const cardRef = ref(null);
const headerRef = ref(null);

const setHeaderWidth = () => {
  if (cardRef.value && headerRef.value) {
    headerRef.value.style.width = `${
      cardRef.value.getBoundingClientRect().width
    }px`;
  }
};

onMounted(() => {
  const resizeObserver = new ResizeObserver(() => {
    setHeaderWidth();
  });
  if (cardRef.value) {
    resizeObserver.observe(cardRef.value);
  }

  onUnmounted(() => {
    resizeObserver.disconnect();
  });
});
</script>

<template>
  <div
    id="price-overview-card"
    ref="cardRef"
    :style="`background-color: ${colors(6)}; border: 1px solid ${colors(
      5
    )}; width: 100%;`"
  >
    <div
      id="price-overview-header"
      ref="headerRef"
      class="to-p"
      :class="{ 'position-fixed': priceOverviewHeaderFixed }"
      :style="`background-color: ${colors(1)};`"
    >
      <h4 style="color: white" class="m-0">
        {{ translate("price_overview") }}
      </h4>
    </div>
    <div
      :style="`border-bottom: ${
        !isFreeTicket || priceOverview.addons.length > 0
          ? `1px solid ${colors(5)}`
          : 'none'
      };`"
      :class="{ 'header-fixed': priceOverviewHeaderFixed, 'header-no-fixed': !priceOverviewHeaderFixed }"
    >
      <div class="d-flex justify-content-between">
        <div class="flex-grow-1">
          <h4 class="m-0">{{ ticket.title }}</h4>
          <p class="mb-0">
            {{ ticket.description }}
          </p>
        </div>
        <Item
          v-if="!isFreeTicket"
          class="text-nowrap"
          :amount="ticket.price.ticket"
          :price_description="
            translate('price_overview_including_vat', { vat_percentage: appStore.event.ticketing_tax })
          "
        >
        </Item>
      </div>
    </div>

    <div
      v-if="priceOverview.addons.length > 0"
      class="to-p d-flex flex-column"
      :style="`border-bottom: 1px solid ${colors(5)}; gap: 5px;`"
    >
      <div
        v-for="(item, index) in priceOverview.addons"
        :key="index"
        class="d-flex justify-content-between flex-grow-1 align-items-center"
      >
        <p class="m-0 addon-desc" :title="item.description || item.name">{{ item.description || item.name }}</p>
        <Item
          :amount="item.amountIncludingTax"
          :price_description="
            translate('price_overview_including_vat', {
              vat_percentage: item.vat,
            })
          "
        >
        </Item>
      </div>
    </div>
    <div
      v-if="!serviceFeeIsZero"
      class="to-p d-flex justify-content-between"
      :style="`border-bottom: 1px solid ${colors(5)};`"
    >
      <p class="m-0">{{ translate("price_overview_service_fee") }}</p>
      <Item
        :amount="props.priceOverview.serviceFee"
        :price_description="
          translate('price_overview_including_vat', { vat_percentage: '21' })
        "
      >
      </Item>
    </div>
    <div v-if="!isFreeTicket" class="to-p d-flex justify-content-between">
      <div>
        <h4 class="m-0">{{ translate("price_overview_total") }}</h4>
      </div>
      <div class="total text-nowrap">
        <h4 class="m-0">{{ props.priceOverview.total }}</h4>
      </div>
    </div>
  </div>
</template>

<style lang="scss" type="text/scss" scoped>
.to-p {
  padding: 5px 10px;
}
.addon-desc {
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-fixed {
  padding: 42px 10px 5px 10px;
}

.header-no-fixed {
  padding: 5px 10px 5px 10px;
}
</style>
